import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dumbbell, Coins, Wallet, Loader2, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCHF = (n) =>
  new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);

const WINDOWS = [
  { key: 'month', label: 'Monat' },
  { key: 'year', label: 'Jahr' },
  { key: 'all', label: 'Gesamt' },
];

const rangeFor = (win) => {
  const now = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (win === 'month') return { start: iso(new Date(now.getFullYear(), now.getMonth(), 1)), end: iso(now) };
  if (win === 'year') return { start: iso(new Date(now.getFullYear(), 0, 1)), end: iso(now) };
  return { start: null, end: null };
};

/**
 * Trainings-Ertrags-Kachel: Ist-Trainings, Punktewert & direkte CHF-Einnahmen
 * pro Zeitfenster, basierend auf durchgeführten Trainings (completed/checked_in).
 */
const TrainingRevenueCard = () => {
  const navigate = useNavigate();
  const [win, setWin] = useState('month');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const { start, end } = rangeFor(win);
        const params = {};
        if (start) params.start_date = start;
        if (end) params.end_date = end;
        const res = await axios.get(`${API}/reports/service-breakdown`, {
          params,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (active) setRows(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (active) setRows([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchData();
    return () => { active = false; };
  }, [win]);

  const totals = useMemo(() => {
    const t = { trainings: 0, pointsValue: 0, directChf: 0 };
    rows.forEach((s) => {
      t.trainings += s.count || 0;
      t.pointsValue += s.points_value_chf ?? s.amount ?? 0;
      t.directChf += s.direct_chf || 0;
    });
    return t;
  }, [rows]);

  const rate = rows[0]?.price_per_point;
  const topServices = useMemo(
    () => rows.filter((s) => (s.count || 0) > 0).slice(0, 3),
    [rows]
  );

  return (
    <Card data-testid="training-revenue-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Dumbbell className="h-5 w-5 text-blue-600" />
            Trainings-Erträge
          </CardTitle>
          <div className="flex gap-1">
            {WINDOWS.map((w) => (
              <Button
                key={w.key}
                size="sm"
                variant={win === w.key ? 'default' : 'outline'}
                className="h-7 px-2 text-xs"
                onClick={() => setWin(w.key)}
                data-testid={`training-rev-window-${w.key}`}
              >
                {w.label}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="rounded-lg bg-blue-50 px-3 py-3">
                <div className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-blue-500">
                  <Dumbbell className="h-3.5 w-3.5" /> Ist-Trainings
                </div>
                <p className="text-2xl font-bold text-blue-700" data-testid="training-rev-count">{totals.trainings}</p>
              </div>
              <div className="rounded-lg bg-amber-50 px-3 py-3">
                <div className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-amber-500">
                  <Coins className="h-3.5 w-3.5" /> Punktewert
                </div>
                <p className="text-2xl font-bold text-amber-700" data-testid="training-rev-points">{fmtCHF(totals.pointsValue)}</p>
              </div>
              <div className="rounded-lg bg-emerald-50 px-3 py-3">
                <div className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-emerald-500">
                  <Wallet className="h-3.5 w-3.5" /> Direkte CHF
                </div>
                <p className="text-2xl font-bold text-emerald-700" data-testid="training-rev-direct">{fmtCHF(totals.directChf)}</p>
              </div>
            </div>

            {topServices.length > 0 && (
              <div className="space-y-1.5 mb-3">
                {topServices.map((s, i) => (
                  <div key={s.service_name || i} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 truncate pr-2">{s.service_name}</span>
                    <span className="text-gray-400 whitespace-nowrap">
                      {s.count}× · {fmtCHF(s.direct_chf || 0)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between border-t pt-2">
              <span className="text-xs text-gray-400">
                {rate ? `Punktwert: CHF ${rate.toFixed(2)}/Punkt` : 'Basierend auf durchgeführten Trainings'}
              </span>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                onClick={() => navigate('/reports')}
                data-testid="training-rev-details-btn"
              >
                Details <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TrainingRevenueCard;
